// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Ressourcer's filters as data: the one set Model, Tabel and Galleri render
 * — the strip is Model's list of it — under the room focus, the search, the
 * Miljøstatus filter, Vigtige and Fejldetektion (all in the URL,
 * `selection.ts`). Also the figures a type shows under a room focus: its
 * parts, quantity and tonnes in that room, the tonnes estimated from the
 * type's when the parts carry none. Pure: no React, no DOM.
 */

import type { SurveyPart, SurveyType } from '../api/types';
import { envFilterOf, roomName as partRoomName, type DismissedPart } from '../kortlaegning/model';
import { formatNumber, formatQuantity } from '../kortlaegning/vocab';
import { massShare, partCountText } from './parts';
import type { Selection } from './selection';

export type ResourceFilter = Pick<Selection, 'room' | 'search' | 'env' | 'starred' | 'errors'>;

/** A type in the set, with its parts that pass the filters (by code). */
export interface TypeMatch {
  type: SurveyType;
  parts: SurveyPart[];
}

/** A part and the type it belongs to: one row of Tabel › Dele, one card of Galleri › Dele and Model's Dele strip. */
export interface PartRow {
  part: SurveyPart;
  type: SurveyType;
}

export interface FilteredResources {
  /** The types, in the survey's order — fejldetektion last, when shown. */
  types: TypeMatch[];
  /** Their passing parts: type by type, by code within a type. */
  parts: PartRow[];
  /** The parts marked as an error under the same filters; empty unless Fejldetektion is on. */
  dismissed: PartRow[];
}

const byCode = (a: SurveyPart, b: SurveyPart) => a.code.localeCompare(b.code);

/** The search as it is matched: trimmed, lower case. */
function needle(f: ResourceFilter): string {
  return f.search.trim().toLowerCase();
}

/** The type-level gates: fejldetektion only with Fejldetektion on, and the Miljøstatus filter. */
function typePasses(t: SurveyType, f: ResourceFilter): boolean {
  return (f.errors || t.review_status !== 'rejected') && (f.env === null || envFilterOf(t.environment_status) === f.env);
}

/** The search hits the type itself: its name, EAK code or EAK name. */
function typeHit(t: SurveyType, q: string): boolean {
  return q === '' || t.name.toLowerCase().includes(q) || t.eak_code.toLowerCase().includes(q) || t.eak_name.toLowerCase().includes(q);
}

/** A part of `t` passes: in the room, hit by the search (through its type, code or room), ★ when Vigtige is on. */
function partPasses(p: SurveyPart, t: SurveyType, f: ResourceFilter, q: string, hit: boolean): boolean {
  return (
    (f.room === null || p.room_id === f.room) &&
    (hit || p.code.toLowerCase().includes(q) || partRoomName(p).toLowerCase().includes(q)) &&
    (!f.starred || t.starred || p.starred)
  );
}

/**
 * `t` under the filters with its passing parts, or null when it is not in
 * the set. A type with no parts at all is kept when the search hits it (and
 * it is ★ under Vigtige), but never under a room focus — it is in no room.
 */
export function matchType(t: SurveyType, f: ResourceFilter): TypeMatch | null {
  if (!typePasses(t, f)) return null;
  const q = needle(f);
  const hit = typeHit(t, q);
  const parts = t.parts.filter((p) => partPasses(p, t, f, q, hit)).sort(byCode);
  if (parts.length > 0) return { type: t, parts };
  if (f.room === null && t.parts.length === 0 && hit && (!f.starred || t.starred)) return { type: t, parts };
  return null;
}

/**
 * The filtered set: `types` (the live survey, `CaseData.types`) in their
 * order — rejected ones after, as Tabel has always listed them — their
 * passing parts, and with Fejldetektion the dismissed parts
 * (`CaseData.dismissed`) under the same filters.
 */
export function filterResources(
  types: readonly SurveyType[],
  dismissed: readonly DismissedPart[],
  f: ResourceFilter,
): FilteredResources {
  const live: TypeMatch[] = [];
  const rejected: TypeMatch[] = [];
  for (const t of types) {
    const m = matchType(t, f);
    if (m) (t.review_status === 'rejected' ? rejected : live).push(m);
  }
  const matched = [...live, ...rejected];
  const parts = matched.flatMap((m) => m.parts.map((part) => ({ part, type: m.type })));
  const q = needle(f);
  const gone = f.errors
    ? dismissed.filter(({ part, type }) => typePasses(type, f) && partPasses(part, type, f, q, typeHit(type, q)))
    : [];
  return { types: matched, parts, dismissed: gone };
}

/** How many filters are set (the room focus counts as one). */
export function activeFilterCount(f: ResourceFilter): number {
  return (
    Number(f.room !== null) + Number(f.search.trim() !== '') + Number(f.env !== null) + Number(f.starred) + Number(f.errors)
  );
}

/** The set's count line: `3 typer · 12 dele`, and ` · 2 fejldetekteret` when any are shown. */
export function countText(set: FilteredResources): string {
  const n = set.types.length;
  const head = `${n === 1 ? '1 type' : `${n} typer`} · ${partCountText(set.parts.length)}`;
  return set.dismissed.length > 0 ? `${head} · ${set.dismissed.length} fejldetekteret` : head;
}

// ---------------------------------------------------------------- figures --

/** A type's figures in a room (or the whole type). `estimated`: the tonnes are partly the type's, shared out. */
export interface PartsFigure {
  parts: number;
  quantity: number;
  /** Tonnes; null when nothing is known or can be estimated. */
  mass: number | null;
  estimated: boolean;
}

/**
 * `type`'s figures in room `room` (null = the whole type, as the server
 * sums it). The tonnes are the parts' own (`SurveyPart.mass_t`); parts
 * without them get their share of the type's tonnes (`massShare`: by
 * quantity, by count for stk) and the figure reads as estimated. Only the
 * room scopes it — not the search or Vigtige.
 */
export function roomFigure(type: SurveyType, room: number | null): PartsFigure {
  if (room === null) return { parts: type.parts.length, quantity: type.quantity, mass: type.mass_t, estimated: false };
  const parts = type.parts.filter((p) => p.room_id === room);
  const quantity = parts.reduce((s, p) => s + p.quantity, 0);
  let mass: number | null = null;
  let estimated = false;
  const missing: SurveyPart[] = [];
  for (const p of parts) {
    if (typeof p.mass_t === 'number') mass = (mass ?? 0) + p.mass_t;
    else missing.push(p);
  }
  if (missing.length > 0 && type.mass_t !== null) {
    mass = (mass ?? 0) + massShare(type, missing);
    estimated = true;
  }
  return { parts: parts.length, quantity, mass, estimated };
}

/** The figure's tonnes: `≈ 5,2 t` when estimated, `5,2 t`, two decimals under 1 t; `–` when unknown. */
export function massText(f: PartsFigure): string {
  if (f.mass === null) return '–';
  const t = `${formatNumber(f.mass, f.mass < 1 ? 2 : 1)} t`;
  return f.estimated ? `≈ ${t}` : t;
}

/** The inspector's room line: `I Stue: 3 dele · 26,9 m² · ≈ 5,2 t`, or `I Stue: ingen dele`. */
export function roomShareText(roomName: string, f: PartsFigure, unit: string): string {
  if (f.parts === 0) return `I ${roomName}: ingen dele`;
  const bits = [partCountText(f.parts), formatQuantity(f.quantity, unit)];
  if (f.mass !== null) bits.push(massText(f));
  return `I ${roomName}: ${bits.join(' · ')}`;
}
